import { addDays, addMonths, differenceInCalendarDays, differenceInCalendarMonths, format, parseISO, startOfMonth } from 'date-fns';
import type { SeActivityMetric } from '../hooks/useSfTeamActivitySummary';

export interface ProfileStat {
  val: string;
  lbl: string;
  sub: string;
}

export interface MonthlyEngagement {
  month: string;
  demoPitch: number;
  design: number;
  adoption: number;
  pov: number;
  other: number;
}

export interface MonthRange {
  key: string;
  label: string;
  filter: string;
}

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const pad = (n: number) => String(n).padStart(2, '0');
const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** Up to two decimals, trailing zeros dropped: 4250 -> "$4.25K", 4300 -> "$4.3K", 2,000,000 -> "$2M". */
export const fmtMoney = (n: number): string => {
  const trim = (v: number) => String(Number(v.toFixed(2)));
  if (n >= 999_995) return `$${trim(n / 1_000_000)}M`;
  if (n >= 1_000) return `$${trim(n / 1_000)}K`;
  return `$${trim(n)}`;
};

/** The caller's own opportunities from `sf/team/opportunities` (full view), keyed by lower-cased email. */
export function ownOpportunities(data: any, email: string | null | undefined): any[] {
  const key = String(email || '').toLowerCase();
  const list = key ? data?.[key]?.opportunities : null;
  return Array.isArray(list) ? list : [];
}

/** Four profile tiles from open opportunities; `loaded=false` renders placeholders rather than zeros. */
export function buildOpportunityStats(opps: any[], loaded: boolean, now: Date = new Date()): ProfileStat[] {
  if (!loaded) {
    return [
      { val: '—', lbl: 'Active Customers', sub: '' },
      { val: '—', lbl: 'Pipeline Value', sub: '' },
      { val: '—', lbl: 'Open Opportunities', sub: '' },
      { val: '—', lbl: 'Closing This Month', sub: '' },
    ];
  }
  const accounts = new Set(opps.map((o) => o?.AccountId || o?.AccountName).filter(Boolean));
  const pipeline = opps.reduce((sum, o) => sum + (Number(o?.Amount) || 0), 0);
  // Still to come this month: a close date from today through the end of the current month.
  const today = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  const closing = opps.filter((o) => {
    const d = o?.CloseDate ? String(o.CloseDate).slice(0, 10) : '';
    return d >= today && d.slice(0, 7) === today.slice(0, 7);
  }).length;
  const total = opps.length;
  return [
    { val: String(accounts.size), lbl: 'Active Customers', sub: `with ${total} open ${total === 1 ? 'opportunity' : 'opportunities'}` },
    { val: fmtMoney(pipeline), lbl: 'Pipeline Value', sub: `across ${total} open ${total === 1 ? 'opportunity' : 'opportunities'}` },
    { val: String(total), lbl: 'Open Opportunities', sub: 'where you are on the team' },
    { val: String(closing), lbl: 'Closing This Month', sub: `of ${total} open` },
  ];
}

/** Calendar-month windows for the last `months` months, oldest first, as `range:` filters for the activity summary. */
export function monthRanges(now: Date, months: number): MonthRange[] {
  const out: MonthRange[] = [];
  for (let i = months - 1; i >= 0; i--) {
    const start = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const end = new Date(start.getFullYear(), start.getMonth() + 1, 0);
    out.push({
      key: `${start.getFullYear()}-${pad(start.getMonth() + 1)}`,
      label: MONTH_SHORT[start.getMonth()],
      filter: `range:${ymd(start)}:${ymd(end)}`,
    });
  }
  return out;
}

export type ActivityBucket = Exclude<keyof MonthlyEngagement, 'month'>;
type Bucket = ActivityBucket;


const bucketOf = (name: string): Bucket => {
  const s = name.toLowerCase();
  if (s.includes('pov') || s.includes('poc')) return 'pov';
  if (s.includes('demo') || s.includes('pitch')) return 'demoPitch';
  if (s.includes('design') || s.includes('outcome') || s.includes('whiteboard')) return 'design';
  if (s.includes('adoption') || s.includes('post sales')) return 'adoption';
  return 'other';
};

/** Folds one person's activity counts into the chart's five series, preferring the exact service types. */
export function bucketActivity(label: string, own: SeActivityMetric | null | undefined): MonthlyEngagement {
  const row: MonthlyEngagement = { month: label, demoPitch: 0, design: 0, adoption: 0, pov: 0, other: 0 };
  const exact = own?.service_type_counts;
  const counts = exact && Object.keys(exact).length ? exact : own?.activity_type_counts ?? {};
  for (const [name, n] of Object.entries(counts)) row[bucketOf(name)] += Number(n) || 0;
  return row;
}

export const average = (nums: number[]): number | null =>
  nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : null;

export const percentOf = (num: number | null | undefined, den: number | null | undefined): number | null =>
  den != null && den > 0 && num != null ? (num / den) * 100 : null;

export const clampPercent = (v: number | null | undefined): number => Math.min(100, Math.max(0, v ?? 0));

/** Total activities across the five chart buckets. */
const bucketTotal = (row: MonthlyEngagement) => row.demoPitch + row.design + row.adoption + row.pov + row.other;

type SummaryTree = { own?: SeActivityMetric; reports?: Record<string, { own?: SeActivityMetric }> } | null | undefined;

/**
 * Share of activity that falls in one bucket (e.g. Demo, or Customer Design), over the given monthly trees.
 * `self` is your own share, `team` the average share across direct reports who logged any activity.
 * Returns nulls when a month is still loading, so the radar shows no data rather than a false zero.
 */
export function activityShare(
  trees: SummaryTree[],
  bucket: ActivityBucket,
): { self: number | null; team: number | null; selfCount: number; selfTotal: number } {
  const empty = { self: null, team: null, selfCount: 0, selfTotal: 0 };
  if (!trees.length || trees.some((t) => !t)) return empty;
  const tally = (pick: (t: NonNullable<SummaryTree>) => SeActivityMetric | undefined) => {
    let count = 0;
    let total = 0;
    for (const t of trees) {
      const row = bucketActivity('', pick(t as NonNullable<SummaryTree>));
      count += row[bucket];
      total += bucketTotal(row);
    }
    return { count, total };
  };
  const me = tally((t) => t.own);
  const emails = new Set<string>();
  trees.forEach((t) => Object.keys(t?.reports ?? {}).forEach((e) => emails.add(e)));
  const shares = Array.from(emails)
    .map((e) => tally((t) => t.reports?.[e]?.own))
    .filter((r) => r.total > 0)
    .map((r) => (r.count / r.total) * 100);
  return {
    self: me.total > 0 ? (me.count / me.total) * 100 : null,
    team: average(shares),
    selfCount: me.count,
    selfTotal: me.total,
  };
}

export const ACTIVITY_BUCKETS: ActivityBucket[] = ['demoPitch', 'design', 'pov', 'adoption', 'other'];

export const activityBucket = (task: any): ActivityBucket => bucketOf(String(task?.SE_Service_Type__c || ''));

export interface OppActivityItem {
  id: string;
  date: string | null;
  subject: string;
  status: string;
  bucket: ActivityBucket;
}

export interface OppActivityRow {
  id: string;
  name: string;
  account: string;
  stage: string;
  closeDate: string | null;
  amount: number | null;
  count: number;
  lastActivity: string | null;
  mix: Record<ActivityBucket, number>;
  activities: OppActivityItem[];
}

export interface OppActivityFilters {
  /** Keep only these activity types; empty or absent keeps all. */
  types?: ReadonlySet<ActivityBucket>;
  /** Case-insensitive match on opportunity or account name. */
  query?: string;
}

export type OppActivitySortKey = 'name' | 'stage' | 'closeDate' | 'amount' | 'count' | 'lastActivity';

const emptyMix = (): Record<ActivityBucket, number> => ({ demoPitch: 0, design: 0, pov: 0, adoption: 0, other: 0 });

/**
 * One row per opportunity that has SE activity inside `range` (inclusive YYYY-MM-DD, null = all time).
 * The window is applied here on `ActivityDate`, so changing the period never needs another request.
 * `idle` counts opportunities the person is on that had no activity in the window.
 */
export function buildOppActivityRows(
  opps: any[],
  range: { start: string; end: string } | null,
  filters: OppActivityFilters = {},
): { rows: OppActivityRow[]; idle: number } {
  const seen = new Set<string>();
  const rows: OppActivityRow[] = [];
  const q = (filters.query || '').trim().toLowerCase();
  const types = filters.types && filters.types.size ? filters.types : null;
  let idle = 0;
  for (const o of opps) {
    const id = String(o?.id ?? o?.Id ?? '');
    if (!id || seen.has(id)) continue;
    seen.add(id);
    if (q && !`${o?.Name || ''} ${o?.AccountName || ''}`.toLowerCase().includes(q)) continue;
    const tasks: any[] = Array.isArray(o?.se_activities) ? o.se_activities : [];
    const inRange = tasks.filter((t) => {
      const d = String(t?.ActivityDate || '').slice(0, 10);
      return !range || (d && d >= range.start && d <= range.end);
    });
    if (!inRange.length) {
      idle++;
      continue;
    }
    const kept = types ? inRange.filter((t) => types.has(activityBucket(t))) : inRange;
    if (!kept.length) continue;
    const mix = emptyMix();
    let last: string | null = null;
    for (const t of kept) {
      mix[activityBucket(t)]++;
      const d = String(t?.ActivityDate || '').slice(0, 10);
      if (d && (!last || d > last)) last = d;
    }
    const activities: OppActivityItem[] = kept
      .map((t, i) => ({
        id: String(t?.Id || `${id}-${i}`),
        date: t?.ActivityDate ? String(t.ActivityDate).slice(0, 10) : null,
        subject: String(t?.Subject || 'Activity'),
        status: String(t?.Status || ''),
        bucket: activityBucket(t),
      }))
      .sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
    rows.push({
      id,
      name: String(o?.Name || 'Untitled opportunity'),
      account: String(o?.AccountName || ''),
      stage: String(o?.StageName || ''),
      closeDate: o?.CloseDate ? String(o.CloseDate).slice(0, 10) : null,
      amount: o?.Amount != null && Number.isFinite(Number(o.Amount)) ? Number(o.Amount) : null,
      count: kept.length,
      lastActivity: last,
      mix,
      activities,
    });
  }
  return { rows, idle };
}

export function summarizeOppActivity(rows: OppActivityRow[]) {
  const mix = emptyMix();
  let activities = 0;
  let last: string | null = null;
  for (const r of rows) {
    activities += r.count;
    for (const b of ACTIVITY_BUCKETS) mix[b] += r.mix[b];
    if (r.lastActivity && (!last || r.lastActivity > last)) last = r.lastActivity;
  }
  return { opportunities: rows.length, activities, mix, lastActivity: last };
}

/** Sorts a copy; empty values always sink to the bottom whatever the direction. */
export function sortOppRows(rows: OppActivityRow[], key: OppActivitySortKey, dir: 'asc' | 'desc'): OppActivityRow[] {
  const sign = dir === 'asc' ? 1 : -1;
  return rows
    .map((r, i) => ({ r, i }))
    .sort((a, b) => {
      const av = a.r[key];
      const bv = b.r[key];
      const aEmpty = av == null || av === '';
      const bEmpty = bv == null || bv === '';
      if (aEmpty || bEmpty) return aEmpty === bEmpty ? a.i - b.i : aEmpty ? 1 : -1;
      const cmp = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv));
      return cmp ? cmp * sign : a.i - b.i;
    })
    .map((x) => x.r);
}

export interface TimelineBucket {
  key: string;
  label: string;
  detail: string;
  total: number;
  mix: Record<ActivityBucket, number>;
}

/**
 * Activity counts over time for the rows shown: weekly for windows up to ~100 days, monthly beyond.
 * The window ends today (no empty future weeks); with no window it covers the trailing 12 months of activity.
 */
export function activityTimeline(
  rows: OppActivityRow[],
  range: { start: string; end: string } | null,
  today: Date = new Date(),
): TimelineBucket[] {
  const items = rows.flatMap((r) => r.activities).filter((a) => a.date);
  if (!items.length && !range) return [];
  const dates = items.map((a) => a.date as string).sort();
  const todayIso = format(today, 'yyyy-MM-dd');
  let startIso = range ? range.start : dates[0];
  let endIso = range ? (range.end < todayIso ? range.end : todayIso) : dates[dates.length - 1];
  if (!range) {
    const floor = format(startOfMonth(addMonths(parseISO(endIso), -11)), 'yyyy-MM-dd');
    if (startIso < floor) startIso = floor;
  }
  if (endIso < startIso) endIso = startIso;
  const start = parseISO(startIso);
  const end = parseISO(endIso);
  const weekly = differenceInCalendarDays(end, start) <= 100;

  const buckets: TimelineBucket[] = [];
  const blank = () => emptyMix();
  if (weekly) {
    for (let d = start; d <= end; d = addDays(d, 7)) {
      buckets.push({ key: format(d, 'yyyy-MM-dd'), label: format(d, 'MMM d'), detail: `Week of ${format(d, 'MMM d')}`, total: 0, mix: blank() });
    }
  } else {
    for (let d = startOfMonth(start); d <= end; d = addMonths(d, 1)) {
      buckets.push({ key: format(d, 'yyyy-MM'), label: format(d, 'MMM'), detail: format(d, 'MMMM yyyy'), total: 0, mix: blank() });
    }
  }
  for (const a of items) {
    const d = parseISO(a.date as string);
    if (d < start || d > end) continue;
    const i = weekly ? Math.floor(differenceInCalendarDays(d, start) / 7) : differenceInCalendarMonths(d, startOfMonth(start));
    const b = buckets[i];
    if (!b) continue;
    b.total++;
    b.mix[a.bucket]++;
  }
  return buckets;
}
